/**
 * @module cercaliagl/view/VUtils
 */
import { createUid } from '../util';

/**
 * @typedef {Object} SelectOption
 * @property {number|string} value Value
 * @param {string} label Label to show
 */

/**
 * @param {Element} element Element element
 * @param {Element} parent Parent element
 * @return {Object} Relative position from parent
 */
export function getDOMRelativePosition(element, parent) {
  const parentPos = parent.getBoundingClientRect();
  const childrenPos = element.getBoundingClientRect();

  return {
    top: childrenPos.top - parentPos.top,
    right: childrenPos.right - parentPos.right,
    bottom: childrenPos.bottom - parentPos.bottom,
    left: childrenPos.left - parentPos.left,
  };
}

/**
 * @return {boolean} `true` if is mobile else `false`.
 */
export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * @param {string} name Name
 * @param {string} value Value
 * @param {string} label Label
 * @param {boolean} checked Checked
 * @return {string} HTML string
 */
export function createCheckbox(name, value, label, checked) {
  const uid = createUid();
  const idCheckbox = `cb${uid}`;
  const str = `
    <div class="mdc-form-field">
      <div class="mdc-checkbox ${name}">
        <input type="checkbox" id="${idCheckbox}" class="mdc-checkbox__native-control" name="${name}" value="${value}" ${checked ? 'checked' : ''}/>
        <div class="mdc-checkbox__background">
          <svg class="mdc-checkbox__checkmark" viewBox="0 0 24 24">
            <path class="mdc-checkbox__checkmark-path" fill="none" d="M1.73,12.91 8.1,19.28 22.79,4.59"/>
          </svg>
          <div class="mdc-checkbox__mixedmark"></div>
        </div>
        <div class="mdc-checkbox__ripple"></div>
      </div>
      <label for="${idCheckbox}">${label}</label>
    </div>
  `;
  return str;
}

/**
 * @param {Array.<string>} labelButtons Label buttons
 * @return {string} HTML string
 */
export function createTabBar(labelButtons) {
  const str = `
  <div class="mdc-tab-bar" role="tablist">
    <div class="mdc-tab-scroller">
      <div class="mdc-tab-scroller__scroll-area">
        <div class="mdc-tab-scroller__scroll-content">
        ${labelButtons.map((labelButton, i) => `
            <button type="button" class="mdc-tab ${i === 0 ? 'mdc-tab--active' : ''}" role="tab" aria-selected="${i === 0}" tabindex="${i}">
              <span class="mdc-tab__content">
                <span class="mdc-tab__text-label">${labelButton}</span>
              </span>
              <span class="mdc-tab-indicator ${i === 0 ? 'mdc-tab-indicator--active' : ''}">
                <span class="mdc-tab-indicator__content mdc-tab-indicator__content--underline"></span>
              </span>
              <span class="mdc-tab__ripple"></span>
            </button>
          `).join('')}
        </div>
      </div>
    </div>
  </div>
  `;

  return str;
}

/**
 * @param {string} char Char
 * @return {string} HTML circle
 */
export function createCircleStop(char) {
  return `<div class="stop-circle"><span>${char}</span></div>`;
}

/**
 * @param {string} name Radiobox name
 * @param {string} value Radiobox value
 * @param {boolean} isText `true` if text. `fales` if icons
 * @param {string} textOrIcon Text or icon
 * @param {boolean} checked Checked
 * @return {string} html
 */
export function createRadiobox(name, value, isText, textOrIcon, checked) {
  const uid = createUid();
  const idRadiobox = `cb${uid}`;
  const html = `<div class="mdc-form-field ${name}">
    <div class="mdc-radio">
      <input class="mdc-radio__native-control" type="radio" id="${idRadiobox}" name="${name}" value="${value}" ${checked ? 'checked' : ''}>
      <div class="mdc-radio__background">
        <div class="mdc-radio__outer-circle"></div>
        <div class="mdc-radio__inner-circle"></div>
      </div>
      <div class="mdc-radio__ripple"></div>
    </div>
    <label for="${idRadiobox}">${isText ? textOrIcon : `<span class="icon icon-${textOrIcon}"></span>`}</label>
  </div>`;
  return html;
}

/**
 * @param {string} name Name
 * @param {string} text Text
 * @return {string} html
 */
export function createSwitch(name, text) {
  const uid = createUid();
  const idInput = `cb${uid}`;
  const html = `
  <button id="selected-switch" class="mdc-switch mdc-switch--unselected ${name}" type="button" role="switch" aria-checked="false">
    <div class="mdc-switch__track"></div>
    <div class="mdc-switch__handle-track">
      <div class="mdc-switch__handle">
        <div class="mdc-switch__shadow">
          <div class="mdc-elevation-overlay"></div>
        </div>
        <div class="mdc-switch__ripple"></div>
      </div>
    </div>
    <span class="mdc-switch__focus-ring-wrapper">
      <div class="mdc-switch__focus-ring"></div>
    </span>
  </button>
  ${text ? `<label for="${idInput}">${text}</label>` : ''}`;
  return html;
}

/**
 * @param {string} type Type
 * @param {string} name Name
 * @param {string} [defVal] Default value
 * @return {string} html
 */
function createInputInner(type, name, defVal) {
  const html = `<div class="mdc-text-field mdc-text-field--outlined ${name}">
    <input class="mdc-text-field__input" type="${type}" name="${name}" ${defVal ? `value="${defVal}"` : ''}>
    <div class="mdc-notched-outline">
      <div class="mdc-notched-outline__leading"></div>
      <div class="mdc-notched-outline__trailing"></div>
    </div>
  </div>`;
  return html;
}

/**
 * @param {string} name Name
 * @param {string} [defVal] Default value
 * @return {string} html
 */
export function createInputDate(name, defVal) {
  return createInputInner('date', name, defVal);
}

/**
 * @param {string} name Name
 * @param {string} [defVal] Default value
 * @return {string} html
 */
export function createInputTime(name, defVal) {
  return createInputInner('time', name, defVal);
}

/**
 * @param {string} name Name
 * @param {string} labelText Label floating. If null. No Input text
 * @param {string} spanIcon Span icon. For example `<span class="icon-gps"/>`
 * @param {boolean} [checked] Checked
 * @param {boolean} [inputField] inputField
 * @return {string} html
 */
export function createCheckboxIconInput(name, labelText, spanIcon, checked = false, inputField = true) {
  const uid = createUid();
  const idCheckbox = `cb${uid}`;
  const idInput = `input${uid}`;
  const html = `<div class="${name} mdc-form-field">
    <div class="mdc-checkbox">
      <input type="checkbox" id="${idCheckbox}" class="mdc-checkbox__native-control" name="${name}" ${checked ? 'checked' : ''}/>
      <div class="mdc-checkbox__background">
        <svg class="mdc-checkbox__checkmark" viewBox="0 0 24 24">
          <path class="mdc-checkbox__checkmark-path" fill="none" d="M1.73,12.91 8.1,19.28 22.79,4.59"/>
        </svg>
        <div class="mdc-checkbox__mixedmark"></div>
      </div>
      <div class="mdc-checkbox__ripple"></div>
    </div>
    <div class="icon">
      ${spanIcon}
    </div>
    ${inputField ? `
    <label class="${name} mdc-text-field mdc-text-field--filled">
      <span class="mdc-text-field__ripple"></span>
      <span class="mdc-floating-label" id="${idInput}">${labelText}</span>
      <input name="${name}" class="mdc-text-field__input" type="text" aria-labelledby="${idInput}">
      <span class="mdc-line-ripple"></span>
    </label>
    `
    : `<label for="${idCheckbox}">${labelText}</label>`}
  </div>`;
  return html;
}

/**
 * @return {string} HTML progress bar indeterminate
 */
export function createProgressBar() {
  const html = `
    <div role="progressbar" class="progressbar mdc-linear-progress mdc-linear-progress--indeterminate">
      <div class="mdc-linear-progress__buffering-dots"></div>
      <div class="mdc-linear-progress__buffer"></div>
      <div class="mdc-linear-progress__bar mdc-linear-progress__primary-bar"><span class="mdc-linear-progress__bar-inner"></span></div>
      <div class="mdc-linear-progress__bar mdc-linear-progress__secondary-bar"><span class="mdc-linear-progress__bar-inner"></span></div>
    </div>`;
  return html;
}

/**
 * @param {string} name Name
 * @param {number} min Minimum
 * @param {number} max Maximum
 * @param {number} currentValue Current value
 * @param {boolean} discrete With pin
 * @param {string} [suffix] Sufix
 * @return {string} HTML slider
 */
export function createSlider(name, min, max, currentValue, discrete, suffix) {
  return `
  <div class="mdc-slider ${discrete ? 'mdc-slider--discrete' : ''} ${name}" data-step="1">
    <input class="mdc-slider__input" type="range" min="${min}" max="${max}" value="${currentValue}">
    <div class="mdc-slider__track">
      <div class="mdc-slider__track--inactive"></div>
      <div class="mdc-slider__track--active">
        <div class="mdc-slider__track--active_fill"></div>
      </div>
    </div>
    <div class="mdc-slider__thumb" role="slider" tabindex="0" aria-label="Select Value" aria-valuemin="${min}" aria-valuemax="${max}" aria-valuenow="${currentValue}">
    <div class="mdc-slider__value-indicator-container">
        <div class="mdc-slider__value-indicator">
          <span class="mdc-slider__value-indicator-text">${currentValue}</span>
        </div>
      </div>
      <div class="mdc-slider__thumb-knob"></div>
      ${suffix ? `<span class="value">${currentValue} ${suffix}</span>` : ''}
    </div>
  </div>`;
}

/**
 * @param {string} [label] Optional label
 * @return {string}
 */
function createSelectAnchorDiv(label) {
  return `
  <div class="mdc-select__anchor">
    <span class="mdc-select__ripple"></span>
    ${label ? `<span class="mdc-floating-label mdc-floating-label--float-above">${label}</span>` : ''}
    <span class="mdc-select__selected-text-container">
      <span class="mdc-select__selected-text"></span>
    </span>
    <span class="mdc-select__dropdown-icon">
      <svg class="mdc-select__dropdown-icon-graphic" viewBox="7 10 10 5" focusable="false">
        <polygon class="mdc-select__dropdown-icon-inactive" stroke="none" fill-rule="evenodd" points="7 10 12 15 17 10" />
        <polygon class="mdc-select__dropdown-icon-active" stroke="none" fill-rule="evenodd" points="7 15 12 10 17 15" />
      </svg>
    </span>
    <span class="mdc-line-ripple"></span>
  </div>`;
}

/**
 * @param {SelectOption} option
 * @param {string|number} selectedValue
 * @return {string} HTML code for element
 */
export function createSelectOptionItem(option, selectedValue) {
  return `
    <li class="mdc-list-item  ${option.value === selectedValue ? 'mdc-list-item--selected' : ''}" aria-selected="${option.value === selectedValue ? 'true' : 'false'}" data-value="${option.value}">
      <span class="mdc-list-item__ripple"></span>
      <span class="mdc-list-item__text">${option.label}</span>
    </li>`;
}

/**
 * @param {string} name Name
 * @param {Array.<SelectOption>} options Options
 * @param {string} selectedValue
 * @param {string} [label]
 * @return {string} HTML code for element
 */
export function createSelect(name, options, selectedValue, label) {
  return `
  <div class="${name} mdc-select mdc-select--filled">
    ${createSelectAnchorDiv(label)}
    <div class="mdc-select__menu mdc-menu mdc-menu-surface">
      <ul class="mdc-list">
        ${options.map((option) => createSelectOptionItem(option, selectedValue)).join('')}
      </ul>
    </div>
  </div>`;
}

/**
 * @param {string} name Name
 * @param {string} label Label
 * @return {string} HTML code for element
 */
export function createFilledTextField(name, label) {
  const uid = `${name}${createUid()}`;
  return `<div class="mdc-form-field">
      <label class="${name} mdc-text-field mdc-text-field--filled">
        <span class="mdc-text-field__ripple"></span>
        <span class="mdc-floating-label" id="${uid}">${label}</span>
        <input class="mdc-text-field__input" type="text" aria-labelledby="${uid}">
        <span class="mdc-line-ripple"></span>
      </label>
    </div>`;
}

/**
 * @param {string} name Name
 * @param {string} label Label
 * @param {boolean} [raised] Default
 * @return {string} HTML code for element
 */
export function createTextButton(name, label, raised = true) {
  return `
    <button type="button" class="${name} mdc-button ${raised ? 'mdc-button--raised' : ''}">
      <span class="mdc-button__ripple"></span>
      <span class="mdc-button__label">${label}</span>
    </button> 
  `;
}

/**
 * @param {string} className Classname
 * @param {string} name Name property
 * @param {string} iconSuffix Icon suffix. Prefix is 'icon-'
 * @return {string} HTML code for element
 */
export function createIconButton(className, name, iconSuffix) {
  return `
  <button type="button" class="mdc-icon-button ${className} ${name}" ${name ? `name=${name}` : ''}>
    <span class="mdc-icon-button__icon icon-${iconSuffix}"></span>
  </button> 
  `;
}

/**
 * @param {string} className Class name
 * @param {string} name Name
 * @param {string} label Label
 * @param {string} [icon] Icon
 * @param {string} [defVal] defVal
 * @return {string} HTML code for element
 */
export function createTextFieldWithSuffix(className, label, suffix, icon, defVal) {
  const inputId = `textinput${createUid()}`;
  return `
    <div class="${className} mdc-text-field mdc-text-field--outlined mdc-text-field--with-leading-icon mdc-text-field--with-trailing-icon">
      <div style="display:table">
        <span class="prefix icon icon-${icon}"></span>
        <input class="mdc-text-field__input" id="${inputId}" value="${defVal}" >
        <span class="suffix">${suffix}</span>
      </div>
      <div class="mdc-notched-outline mdc-notched-outline--upgraded">
        <div class="mdc-notched-outline__leading"></div>
        <div class="mdc-notched-outline__trailing"></div>
      </div>
    </div>
  `;
}

/**
 * @param {string} inputDate Input date
 * @return {string} Formatted date
 */
export function formatDate(inputDate) {
  const mm = inputDate.getMonth() + 1;
  const dd = inputDate.getDate();

  return `${inputDate.getFullYear()}-${mm > 9 ? '' : '0'}${mm}-${dd > 9 ? '' : '0'}${dd}`;
}

export default {};
